import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setDemoSession, setDemoAnonymous } from '../lib/session';

// Holiday Inn HOTEL per-role entry — no-login. One component, five roles:
// guest · concierge · valet · security · housekeeping · manager. Each mints the
// role's OWN session from the backend and lands directly in the existing view
// (Hotel Assistant / Staff Hub / Command Center). On failure it shows a VISIBLE
// error card — never a silent /login eject.
const API = (typeof window !== 'undefined' ? window.location.origin : '');
const RETURN_URL = '/demo/hotel';
export const HOTEL_DEMO_FLAG = 'yulontap_hotel_demo';
export const HOTEL_DEMO_STAFF = 'yulontap_hotel_demo_staff';
// Tells the Assistant's registration screen to offer the one-tap demo guest.
export const GUEST_OFFER_FLAG = 'yulontap_hotel_demo_offer_guest';
const STAFF_ROLES = ['concierge', 'valet', 'security', 'housekeeping'];
const GUEST_CARD = 'HARBOURVIEW1';

/**
 * A DEMO IS ONE CONTINUOUS STORY, so re-entering a role must RESUME it.
 *
 * The guest launcher used to mint a synthetic stay and overwrite
 * `localStorage['yulontap_stay:<org>']` on EVERY entry. So a presenter who
 * registered themselves (room 105, their name, their PIN) and then switched to
 * Security and back lost that guest: the store now held a different token —
 * and when the launcher's org guess didn't match the org the Assistant reads
 * for (it derives the key from /info), the store held NOTHING for that key and
 * the guest was dumped back on "What room are you staying in?".
 *
 * Now: any existing stay in this browser is validated and REUSED, untouched.
 * Only Reset demo (or the guest signing out) starts a new guest.
 */
const findLiveStay = async (cardId) => {
    let keys = [];
    try {
        keys = Object.keys(localStorage).filter((k) => k.startsWith('yulontap_stay:')
            || k === `yulontap_assistant:${cardId}`);
    } catch (_e) { return null; }
    for (const k of keys) {
        let token = null;
        try { token = (JSON.parse(localStorage.getItem(k)) || {}).token; } catch (_e) { /* skip */ }
        if (!token) continue;
        try {
            const r = await fetch(`${API}/api/assistant/${cardId}/session/${token}`);
            if (r.ok) return { key: k, token };
        } catch (_e) { /* try the next one */ }
    }
    return null;
};

export default function DemoHotelEntry({ role }) {
    const { t } = useTranslation();
    const [error, setError] = useState(null);

    const enter = useCallback(async () => {
        setError(null);
        try { localStorage.setItem('yulontap-theme-preference', 'dark'); } catch (_e) { /* ignore */ }
        document.documentElement.classList.add('dark');
        try {
            sessionStorage.setItem(HOTEL_DEMO_FLAG, '1');
            sessionStorage.removeItem('yulontap_auto_demo');
            sessionStorage.setItem('yulontap_demo_return', RETURN_URL);
            if (role === 'manager') {
                const res = await fetch(`${API}/api/demo-hotel/manager/session?return_url=${encodeURIComponent(RETURN_URL)}`);
                const d = await res.json().catch(() => ({}));
                if (!res.ok || !d.access_token) throw new Error(`HTTP ${res.status}${d.detail ? ` — ${d.detail}` : ''}`);
                setDemoSession(d.access_token, d.user || {});
                sessionStorage.setItem('yulontap_demo_mode', '1');
                sessionStorage.removeItem(HOTEL_DEMO_STAFF);
                window.location.replace('/command-center');
                return;
            }
            if (role === 'guest') {
                setDemoAnonymous();
                sessionStorage.removeItem('yulontap_demo_mode');
                sessionStorage.removeItem(HOTEL_DEMO_STAFF);
                // RESUME the guest already in this browser — same room, name,
                // PIN, language, requests and statuses.
                const live = await findLiveStay(GUEST_CARD);
                if (live) {
                    // Land on the guest's HOME, not whichever sub-screen they
                    // were on when they left. A role switch is a fresh look at
                    // the guest's world — home is where their requests and
                    // statuses are listed. Token/lang/PIN untouched.
                    try {
                        const cur = JSON.parse(localStorage.getItem(live.key)) || {};
                        localStorage.setItem(live.key, JSON.stringify({ ...cur, phase: 'menu', switchResume: true }));
                    } catch (_e) { /* the stay still resumes */ }
                    sessionStorage.removeItem(GUEST_OFFER_FLAG);
                    window.location.replace(`/assistant/${GUEST_CARD}`);
                    return;
                }
                // No guest yet: land on the REAL registration screen and let it
                // offer one-tap "Continue as demo guest". The Assistant writes
                // the stay itself, using the org from its own /info call, so the
                // store key can never disagree with what it reads.
                sessionStorage.setItem(GUEST_OFFER_FLAG, '1');
                window.location.replace(`/assistant/${GUEST_CARD}`);
                return;
            }
            // Staff: mint the DEPARTMENT's own staff session, hand it to the
            // existing Staff Hub (which scopes itself by the capability array).
            setDemoAnonymous();
            sessionStorage.removeItem('yulontap_demo_mode');
            // Same rule as the guest: re-entering a department the presenter
            // already opened keeps THAT session instead of minting a new one.
            let stored = null;
            try { stored = JSON.parse(sessionStorage.getItem(HOTEL_DEMO_STAFF) || 'null'); } catch (_e) { /* mint */ }
            if (stored && stored.department === role && stored.session?.token) {
                window.location.replace(`/assistant/staff/${stored.card_id}`);
                return;
            }
            const res = await fetch(`${API}/api/demo-hotel/staff/session?department=${encodeURIComponent(role)}&return_url=${encodeURIComponent(RETURN_URL)}`);
            const d = await res.json().catch(() => ({}));
            if (!res.ok || !d.token) throw new Error(`HTTP ${res.status}${d.detail ? ` — ${d.detail}` : ''}`);
            sessionStorage.setItem(HOTEL_DEMO_STAFF, JSON.stringify({ card_id: d.card_id, department: d.department, session: d }));
            window.location.replace(`/assistant/staff/${d.card_id}`);
        } catch (e) {
            setError(e.message || 'Network error');
        }
    }, [role]);

    useEffect(() => { enter(); }, [enter]);

    const roleLabel = t(`hotelDemo.role.${role}`, role);

    if (error) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-200 px-6 text-center"
                data-testid={`demo-hotel-${role}-error`}>
                <AlertTriangle className="w-9 h-9 text-amber-400" />
                <p className="text-base font-semibold text-white">{t('hotelDemo.entryFailedTitle', 'Could not open the hotel demo')}</p>
                <p className="text-sm text-slate-400 max-w-md">{t('hotelDemo.entryFailedBody', 'The demo session could not be created. This is not a login problem — the details below show what failed.')}</p>
                <code className="text-xs text-rose-300 bg-black/40 rounded px-3 py-1.5" data-testid={`demo-hotel-${role}-error-detail`}>{error}</code>
                <div className="flex items-center gap-2 mt-2">
                    <button onClick={enter} data-testid={`demo-hotel-${role}-retry`}
                        className="inline-flex items-center gap-2 text-sm text-white bg-amber-600 hover:bg-amber-500 rounded-full px-4 py-2 transition-colors">
                        <RefreshCw className="w-4 h-4" />{t('hotelDemo.entryRetry', 'Try again')}
                    </button>
                    <a href={RETURN_URL} data-testid={`demo-hotel-${role}-back`}
                        className="inline-flex items-center gap-2 text-sm text-slate-200 border border-white/20 hover:border-white/40 rounded-full px-4 py-2 transition-colors">
                        <ArrowLeft className="w-4 h-4" />{t('hotelDemo.backToLauncher', 'Back to the demo menu')}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-slate-950 text-slate-200"
            data-testid={`demo-hotel-${role}-entry`}>
            <Loader2 className="w-8 h-8 animate-spin text-amber-400" />
            <p className="text-sm text-slate-300">{t('hotelDemo.opening', 'Opening {{role}}…').replace('{{role}}', roleLabel)}</p>
        </div>
    );
}

export { STAFF_ROLES };
